// This utility file provides functions to interact with the blocked emails API.

// Development URL
// const BASE_URL = "https://localhost:7094/WedgeZoningService.svc/rest";
// Production URL
const BASE_URL = "https://www.mchenrycountygis.org/McHenryServiceCore/WedgeZoningService.svc/rest";

/**
 * This function checks if a given email is blocked in the outreach system.
 * @param email The email address to check against the blocked list.
 * @returns
 */
const checkBlockedOutreachEmail = async (email: string): Promise<boolean> => {
  const checkOutreachEmailURL = `${BASE_URL}/CheckOutreachEmail?email=${encodeURIComponent(
    email
  )}`;
  const response = await fetch(checkOutreachEmailURL);
  if (!response.ok) {
    throw new Error(`Failed to check input email: ${response.statusText}`);
  }
  const data = await response.json();
  const { isEmailBlocked } = data;
  return isEmailBlocked;
};

/**
 * This function fetches a list of blocked emails
 * @returns {Promise<string[]>} A promise that resolves to an array of blocked emails
 * @throws {Error} If the fetch request fails
 */
const getBlockedEmailList = async (): Promise<string[]> => {
  const getBlockedEmailsURL = `${BASE_URL}/GetAllBlockedEmails`;
  const response = await fetch(getBlockedEmailsURL);
  if (!response.ok) {
    throw new Error(`Error fetching blocked emails: ${response.statusText}`);
  }
  const data = await response.json();
  return data;
};

/**
 * This function sends a DELETE request to the server to remove the specified email from the blocked list.
 * @param {string} email - The email address to be removed from the blocked list.
 * @returns {Promise<void>} A promise that resolves if/when the email is successfully deleted.
 */
const deleteOutreachEmail = async (
  email: string
): Promise<{ success: boolean; message: string; email?: string }> => {
  const deleteOutreachEmailURL = `${BASE_URL}/DeleteOutreachEmail?email=${encodeURIComponent(
    email
  )}`;
  const response = await fetch(deleteOutreachEmailURL, {
    method: "DELETE",
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(`Error deleting email: ${result.message}`);
  }
  return result;
};

/**
 * This function sends a POST request to the server to add a new email to the blocked list.
 * @param email The email address to be added to the blocked list.
 * @returns A promise that resolves when the email is successfully added.
 */
const addOutReachEmail = async (
  email: string
): Promise<{ success: boolean; message: string; email?: string }> => {
  const addOutreachEmailURL = `${BASE_URL}/AddOutreachEmail?email=${encodeURIComponent(
    email
  )}`;
  const response = await fetch(addOutreachEmailURL, {
    method: "POST",
  });
  const result = await response.json();
  if (!result.success) {
    throw new Error(`Error adding email: ${result.message}`);
  }
  return result;
};

export {
  checkBlockedOutreachEmail,
  getBlockedEmailList,
  deleteOutreachEmail,
  addOutReachEmail,
};
